import { useLocation, useParams } from "react-router";
import { User } from "../../models/user.model";
import React from "react";
import { BankAccount } from "../../models/bank.account.model";
import { CryptoAccount } from "../../models/crypto.account.model";

import AccountFormModal from "../../components/accounts/modals/AccountFormModal";
import DeleteConfirmModal from "../../components/accounts/modals/DeleteConfirmModal";
import { responseHero } from "../../utils/axiosInterceptor";
import { getOrgBankAccounts, getOrgCryptoAccounts, getUserBankAccounts, getUserCryptoAccounts } from "../../api/getAccounts";
import { Organization } from "../../models/organization.model";

export default function AccountHandling() {
    const { id } = useParams();
    const { state } = useLocation();

    let user: User | undefined = undefined;
    let org: Organization | undefined = undefined;

    const [bankAccounts, setBankAccounts] = React.useState<BankAccount[]>([]);
    const [cryptoAccounts, setCryptoAccounts] = React.useState<CryptoAccount[]>([]);

    const [modalType, setModalType] = React.useState<"add" | "edit" | "delete" | null>(null);
    const [accountCategory, setAccountCategory] = React.useState<"bank" | "crypto" | null>(null);
    const [selectedAccount, setSelectedAccount] = React.useState<
        BankAccount | CryptoAccount | null
    >(null);

    if (state && state.user) {
        user = state.user;
    }

    if (state && state.org) {
        org = state.org;
    }

    const [spinner, setSpinner] = React.useState<boolean>(false);

    const fetchAccounts = async () => {
        if (!id) return;
      
        setSpinner(true);
      
        try {
          const [banks, cryptos] = await Promise.all([
            org ? getOrgBankAccounts(Number(id)) : getUserBankAccounts(Number(id)),
            org ? getOrgCryptoAccounts(Number(id)) : getUserCryptoAccounts(Number(id)),
          ]);
      
          setBankAccounts(banks);
          setCryptoAccounts(cryptos);
        } catch (error) {
          console.error("Error fetching accounts:", error);
        } finally {
          setSpinner(false);
        }
      };
      

    React.useEffect(() => {
        fetchAccounts();
    }, [id]);

    // Handlers
    const openAddModal = (type: "bank" | "crypto") => {
        setAccountCategory(type);
        setSelectedAccount(null);
        setModalType("add");
    };

    const openEditModal = (
        type: "bank" | "crypto",
        account: BankAccount | CryptoAccount
    ) => {
        setAccountCategory(type);
        setSelectedAccount(account);
        setModalType("edit");
    };

    const openDeleteModal = (
        type: "bank" | "crypto",
        account: BankAccount | CryptoAccount
    ) => {
        setAccountCategory(type);
        setSelectedAccount(account);
        setModalType("delete");
    };

    const closeModal = () => {
        setModalType(null);
        setAccountCategory(null);
        setSelectedAccount(null);
    };

    const updateActiveAccount = async (id: number, state: string) => {

        if(!id) return;

        try {
            const response = await responseHero.put(`${process.env.REACT_APP_API_URL}/updateToDefault/${state}/${id}`);

            console.log("Alapértelmezett számla frissítve:", response.data);

            fetchAccounts();
        } catch (error: any) {
            console.error(error?.response?.data || error);
        }
    }

    const handleSaveAccount = (
        formData: Partial<BankAccount> | Partial<CryptoAccount>
    ) => {
        if (!accountCategory || !modalType) return;
    
        let url = "";
        let requestMethod: "post" | "put" = "post";
    
        if (modalType === "add" && accountCategory === "bank") {
            url = `${process.env.REACT_APP_API_URL}/accounts/bank`;
            requestMethod = "post";
        } else if (modalType === "add" && accountCategory === "crypto") {
            url = `${process.env.REACT_APP_API_URL}/accounts/crypto`;
            requestMethod = "post";
        } else if (modalType === "edit" && accountCategory === "bank" && selectedAccount) {
            url = `${process.env.REACT_APP_API_URL}/accounts/bank/${selectedAccount.id}`;
            requestMethod = "put"; // ✅ Uses the account’s own ID
        } else if (modalType === "edit" && accountCategory === "crypto" && selectedAccount) {
            url = `${process.env.REACT_APP_API_URL}/accounts/crypto/${selectedAccount.id}`;
            requestMethod = "put"; // ✅ Uses the account’s own ID
        }
    
        responseHero[requestMethod](url, {
            accData: {
                ...formData,
                ...(user ? { user_id: Number(id) } : { organization_id: Number(id) }),
            },            
        })
            .then((response) => {
                console.log(
                    `${modalType === "add" ? "Created" : "Updated"} ${accountCategory} account:`,
                    response.data
                );
                fetchAccounts(); // ✅ Refresh after success
            })
            .catch((error) => {
                console.error(`Error processing ${accountCategory} account:`, error);
            });
    
        closeModal();
    };
    
    const handleConfirmDelete = () => {
        if (!selectedAccount || !accountCategory) return;
    
        const url =
            accountCategory === "bank"
                ? `${process.env.REACT_APP_API_URL}/accounts/bank/${selectedAccount.id}` // ✅ Uses account ID
                : `${process.env.REACT_APP_API_URL}/accounts/crypto/${selectedAccount.id}`; // ✅ Uses account ID
    
        responseHero
            .delete(url)
            .then((response) => {
                console.log(`${accountCategory} account deleted:`, response.data);
                fetchAccounts();
            })
            .catch((error) => {
                console.error(`Error deleting ${accountCategory} account:`, error);
            });
    
        closeModal();
    };
    
    

    return (
        <div className="m-10">
            <h1 className="text-xl mb-2">
            Számlakezelés:{" "}
            <span className="text-brown font-semibold">
                {user?.name ?? org?.full_name}
            </span>{" "}
            részére.
            </h1>

            {spinner ? <p className="mt-3 text-center text-xl font-semibold">Kérem várjon az adatok betöltésére...</p> : (

            <div className="flex space-x-8">
                {/* BANK TABLE */}
                <div className="w-1/2">
                    <div className="flex items-center justify-between py-2">
                        <h1 className="text-md font-semibold">
                            {state.user?.name ?? state.org?.full_name} -hoz/hez társított bankszámlaszámok listája.
                        </h1>
                        <button
                            className="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700"
                            onClick={() => openAddModal("bank")}
                        >
                            Új bankszámla hozzáadása
                        </button>
                    </div>

                    <table className="min-w-full border border-gray-300">
                        <thead className="bg-gray-100">
                            <tr>
                                <th className="border px-4 py-2">Alapértelmezett</th>
                                <th className="border px-4 py-2">IBAN</th>
                                <th className="border px-4 py-2">Bank neve</th>
                                <th className="border px-4 py-2">Ország</th>
                                <th className="border px-4 py-2">Műveletek</th>
                            </tr>
                        </thead>
                        <tbody>
                            {bankAccounts.map((acc) => (
                                <tr key={acc.id} className="py-2 bg-gray-200 text-center">

                                    <td className="border px-4 py-2">
                                        <input
                                            type="radio"
                                            name="defaultBank"
                                            checked={acc.isDefault === true}
                                            onChange={() => {
                                                updateActiveAccount(acc.id, 'bank');
                                            }}
                                        />
                                    </td>

                                    <td className="border px-4 py-2">{acc?.iban}</td>
                                    <td className="border px-4 py-2">{acc?.name}</td>
                                    <td className="border px-4 py-2">{acc?.country}</td>
                                    <td className="border px-4 py-2 space-x-2">
                                        <button
                                            className="bg-dark-blue text-white px-2 py-1 rounded hover:bg-gray-800"
                                            onClick={() => openEditModal("bank", acc)}
                                        >
                                            Szerkesztés
                                        </button>
                                        <button
                                            className="bg-error text-white px-2 py-1 rounded hover:bg-red-700"
                                            onClick={() => openDeleteModal("bank", acc)}
                                        >
                                            Törlés
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* CRYPTO TABLE */}
                <div className="w-1/2">
                    <div className="flex items-center justify-between py-2">
                        <h1 className="text-md font-semibold">
                            {user?.name ?? org?.full_name} -hoz/hez társított cryptotárcák listája.
                        </h1>
                        <button
                            className="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700"
                            onClick={() => openAddModal("crypto")}
                        >
                            Új cryptotárca hozzáadása
                        </button>
                    </div>

                    <table className="min-w-full border border-gray-300">
                        <thead className="bg-gray-100">
                            <tr>
                                <th className="border px-4 py-2">Alapértelmezett</th>
                                <th className="border px-4 py-2">Hálózat</th>
                                <th className="border px-4 py-2">Hálózati cím</th>
                                <th className="border px-4 py-2">Tőzsde neve</th>
                                <th className="border px-4 py-2">Műveletek</th>
                            </tr>
                        </thead>
                        <tbody>
                            {cryptoAccounts.map((acc) => (
                                <tr key={acc.id} className="py-2 bg-gray-200 text-center">

                                    <td className="border px-4 py-2">
                                        <input
                                            type="radio"
                                            name="defaultCrypto"
                                            checked={acc.isDefault === true}
                                            onChange={() => {
                                                updateActiveAccount(acc.id, 'crypto');
                                            }}
                                        />
                                    </td>

                                    <td className="border px-4 py-2">{acc?.network}</td>
                                    <td className="border px-4 py-2">{acc?.address}</td>
                                    <td className="border px-4 py-2">{acc?.exchange}</td>
                                    <td className="border px-4 py-2 space-x-2">
                                        <button
                                            className="bg-dark-blue text-white px-2 py-1 rounded hover:bg-gray-800"
                                            onClick={() => openEditModal("crypto", acc)}
                                        >
                                            Szerkesztés
                                        </button>
                                        <button
                                            className="bg-error text-white px-2 py-1 rounded hover:bg-red-700"
                                            onClick={() => openDeleteModal("crypto", acc)}
                                        >
                                            Törlés
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
            )}

            {/* === Render Modals Conditionally === */}
            {modalType !== null && accountCategory !== null && modalType !== "delete" && (
                <AccountFormModal
                    type={accountCategory}
                    mode={modalType}
                    initialValues={selectedAccount}
                    onSubmit={handleSaveAccount}
                    onClose={closeModal}
                />
            )}

            {modalType === "delete" && selectedAccount && (
                <DeleteConfirmModal
                    itemName={
                        accountCategory === "bank"
                            ? (selectedAccount as BankAccount).iban
                            : (selectedAccount as CryptoAccount).address
                    }
                    onConfirm={handleConfirmDelete}
                    onClose={closeModal}
                />
            )}
        </div>
    );
}
